import {useEffect, useRef, useState} from 'react';
import css from './note.module.css';
import {Icon} from '../common/Icon';
import {formatDuration} from '../../utils';
import type {Note} from '../../types/Note';
import {getNoteDownloadUrl} from '../../services/storageService';

export function AudioPlayer({note}: {note: Note}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState<string | null>(note.audioUrl ?? null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(note.duration || 0);

  useEffect(() => {
    let cancelled = false;
    if (!note.audioUrl && note.storagePath) {
      getNoteDownloadUrl(note.storagePath)
        .then(url => {
          if (!cancelled) {
            setSrc(url);
          }
        })
        .catch(() => {});
    } else {
      setSrc(note.audioUrl ?? null);
    }
    return () => {
      cancelled = true;
    };
  }, [note.audioUrl, note.storagePath]);

  // Space toggles playback when not typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'BUTTON' ||
        target.isContentEditable;
      if (e.code === 'Space' && !typing) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!src) {
    return null;
  }

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  };

  return (
    <div className={`${css.player} no-print`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={e => setPosition(e.currentTarget.currentTime)}
        onLoadedMetadata={e => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) {
            setDuration(d);
          }
        }}
      />
      <button className={css.playBtn} onClick={toggle} type="button">
        <Icon name={playing ? 'pause' : 'play'} size={18} />
      </button>
      <span className={css.playerTime}>{formatDuration(position)}</span>
      <input
        className={css.seek}
        type="range"
        min={0}
        max={duration || 1}
        step={0.1}
        value={Math.min(position, duration || position)}
        onChange={e => {
          const value = Number(e.target.value);
          if (audioRef.current) {
            audioRef.current.currentTime = value;
          }
          setPosition(value);
        }}
      />
      <span className={css.playerTime}>{formatDuration(duration)}</span>
    </div>
  );
}
